<script lang="ts">
import { defineComponent } from 'vue';
import { useFavoritesStore } from '@/stores/favorites';
import { mapActions, mapState, mapWritableState } from 'pinia';
import MultiDropDown from '@/components/popular/multi-drop-down.vue';
import PopularCompetitions from '@/components/popular/popular-competitions.vue';
import PopularFixtures from '@/components/popular/popular-fixtures.vue';
import Competitions from '@/components/popular/competitions.vue';
import Pagination from '@/components/pagination.vue';
import Fixtures from '@/components/popular/fixtures.vue';
import Headline from '@/components/headline.vue';

export default defineComponent({
  name: 'popular-fixtures-page',
  components: {
    Fixtures,
    Competitions,
    MultiDropDown,
    PopularCompetitions,
    PopularFixtures,
    Pagination,
    Headline,
  },
  computed: {
    ...mapState(useFavoritesStore, [
      'sports',
      'regions',
      'popularFixtures',
      'popularFixturesCount',
      'fixtures',
      'fixturesCount',
    ]),
    ...mapWritableState(useFavoritesStore, ['fixturesOffset', 'fixturesPageSize']),
  },
  data() {
    return {
      selectedSportIds: [] as number[],
      selectedRegionIds: [] as number[],
      selectedCompetitionIds: [] as number[],
    };
  },
  methods: {
    ...mapActions(useFavoritesStore, [
      'fetchSports',
      'fetchRegions',
      'fetchPopularFixtures',
      'fetchFixtures',
    ]),
    fetchFixturesWithFilters() {
      this.fetchFixtures({
        sportIds: this.selectedSportIds.length ? this.selectedSportIds : undefined,
        regionIds: this.selectedRegionIds.length ? this.selectedRegionIds : undefined,
        competitionIds: this.selectedCompetitionIds.length
          ? this.selectedCompetitionIds
          : undefined,
      });
    },
    flipFixturesPage(newOffset: number) {
      this.fixturesOffset = newOffset;
      this.fetchFixturesWithFilters();
    },
    addSportFilter(id: number) {
      if (this.selectedSportIds.find((sportId) => sportId === id)) {
        return;
      }
      this.selectedSportIds.push(id);
      this.fetchFixturesWithFilters();
    },
    addRegionFilter(id: number) {
      if (this.selectedRegionIds.find((regionId) => regionId === id)) {
        return;
      }
      this.selectedRegionIds.push(id);
      this.fetchFixturesWithFilters();
    },
    addCompetitionFilter(id: number) {
      if (this.selectedCompetitionIds.find((competitionId) => competitionId === id)) {
        this.selectedCompetitionIds = this.selectedCompetitionIds.filter((c) => c !== id);
      } else {
        this.selectedCompetitionIds.push(id);
      }
      this.fetchFixturesWithFilters();
    },
    resizeFixturesPage(newSize: number) {
      this.fixturesPageSize = newSize;
      this.fetchFixturesWithFilters();
    },
  },
  mounted() {
    if (!this.sports) {
      this.fetchSports();
    }
    if (!this.regions) {
      this.fetchRegions();
    }
    if (!this.popularFixtures) {
      this.fetchPopularFixtures();
    }
    if (!this.fixtures) {
      this.fetchFixtures();
    }
  },
  watch: {
    selectedSportIds() {
      this.fetchFixturesWithFilters();
    },
    selectedRegionIds() {
      this.fetchFixturesWithFilters();
    },
  },
});
</script>

<template>
  <teleport to="#title">
    <headline class="mx-4 my-2">
      Матчи в "Популярном"
      <template #counter>{{ popularFixturesCount }}</template>
    </headline>
  </teleport>

  <PopularFixtures :fixtures="popularFixtures" />

  <headline class="mx-4 my-2">
    Доступные матчи
    <template #counter>{{ fixturesCount }}</template>
  </headline>

  <div
    class="grid grid-cols-[1fr_3fr_1fr_3fr_80px] gap-3 text-center"
    style="width: 100%; max-width: 100%"
  >
    <div class="place-content-center">Виды спорта</div>
    <MultiDropDown :options="sports" v-model="selectedSportIds" />
    <div class="place-content-center">Регионы</div>
    <MultiDropDown :options="regions" v-model="selectedRegionIds" />
    <button @click="fetchFixturesWithFilters">
      <i class="pi pi-refresh" style="font-size: 2rem" />
    </button>
  </div>
  <Fixtures
    :fixtures="fixtures"
    :addSportFilter="(id: number) => addSportFilter(id)"
    :addRegionFilter="(id: number) => addRegionFilter(id)"
    :addCompetitionFilter="(id: number) => addCompetitionFilter(id)"
  />
  <div class="text-center">
    <Pagination
      :offset="fixturesOffset"
      :size="fixturesPageSize"
      :count="fixturesCount"
      :resizable="true"
      @flip="flipFixturesPage"
      @resize="resizeFixturesPage"
    />
  </div>
</template>

<style scoped lang="scss"></style>
